import { useState, useCallback } from "react";

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface UsePaginationOptions {
  initialPage?: number;
  initialLimit?: number;
  initialTotal?: number;
}

export const usePagination = (options: UsePaginationOptions = {}) => {
  const { initialPage = 1, initialLimit = 10, initialTotal = 0 } = options;

  const [pagination, setPagination] = useState<PaginationState>({
    page: initialPage,
    limit: initialLimit,
    total: initialTotal,
    hasMore: initialTotal > initialLimit,
  });

  const setPage = useCallback((page: number) => {
    setPagination((prev) => ({
      ...prev,
      page,
      hasMore: page * prev.limit < prev.total,
    }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1, // Reset to first page when changing limit
      hasMore: limit < prev.total,
    }));
  }, []);

  const setTotal = useCallback((total: number) => {
    setPagination((prev) => ({
      ...prev,
      total,
      hasMore: prev.page * prev.limit < total,
    }));
  }, []);

  return {
    pagination,
    setPage,
    setLimit,
    setTotal,
  };
};
